import { Maybe, maybe, mt } from '@passionware/monads';
import { ReportTemplateEditorDialog } from 'v5/features/reporting/ReportTemplateEditorDialog';
import { WithBookingTypeService } from 'v5/services/io/BookingTypeService/BookingTypeService';
import { DownloadReportDialog } from 'v5/features/reporting/DownloadReportDialog';
import { WithReportTemplateMutationService } from 'v5/services/io/ReportTemplateMutationService/ReportTemplateMutationService';
import { ensureError } from '@passionware/platform-js';
import {
  MetastructureField,
  MetastructureFieldType,
  Shortlist
} from '__types__';
import { Info } from 'lucide-react';
import {
  openContactSidebar,
  closeContactSidebar,
  openModal,
  openBookingSidebar,
  openProductionSidebar
} from 'store/v1/ui/ui.actions';
import { CreateCallsheetDialog } from 'v5/features/callsheets/CreateCallsheetDialog';
import { DuplicateCallsheetDialog } from 'v5/features/callsheets/DuplicateCallsheetDialog';
import { DuplicateCallsheetFormData } from 'v5/features/callsheets/types';
import { CreateCalloutDialog } from 'v5/features/callouts/CreateCalloutDialog';
import { EditCalloutDialog } from 'v5/features/callouts/EditCalloutDialog';
import { DuplicateCalloutDialog } from 'v5/features/callouts/DuplicateCalloutDialog';
import {
  CalloutCreateFormData,
  CalloutDuplicateFormData
} from 'v5/features/callouts/callout-create.types';
import { getCalloutPublicUrl } from 'v5/api/callouts/callout-slug.logic';
import {
  getCalloutDetailPath,
  getContactDetailPath,
  getShortlistDetailPathAsync,
  getCallsheetDetailPath,
  getCallsheetDetailPathAsync
} from 'v5/core/app-scope/entityWorkspaceRouting';
import {
  appScopeUtils,
  type AppScope,
  type AppScopeIdentity
} from 'v5/core/app-scope/appScope.types';
import { Callout } from '__types__/v1/models/Callout';
import { CustomFieldAddDialogWidget } from 'v5/features/custom-fields/config-dialog/CustomFieldAddDialogWidget';
import { myRouting } from 'v5/features/core/routing';
import { WithServices } from 'v5/platform/ts/services';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { WithProductionTypeService } from 'v5/services/internal/ProductionTypeService/ProductionTypeService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithGroupsService } from 'v5/services/io/GroupsService/GroupsService';
import { WithProductionService } from 'v5/services/io/ProductionService/ProductionService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';
import store from '../../../../store';
import {
  getCustomFieldDefinitions,
  getCustomFieldGroupDefinitions
} from 'store/v1/custom_fields/custom_fields.selectors';
import { CORE_STRUCTURES } from '../../../../v1/helpers/coreStructures';
import { friendlyStringFromConst } from 'v1/helpers/misc';
import type {
  CreateCustomFieldScope,
  ScopedToCoreStructureRequest
} from 'v5/services/internal/MutationService/MutationService';
import type {
  AssignCustomFieldsDialogRequest,
  UserFlowService
} from './UserFlowService';
import { WithCallsheetService } from 'v5/services/io/CallsheetService/CallsheetService';
import { WithCallsheetMutationService } from 'v5/services/io/CallsheetMutationService/CallsheetMutationService';
import { WithCalloutMutationService } from 'v5/services/io/CalloutMutationService/CalloutMutationService';
import { WithCalloutsService } from 'v5/services/io/CalloutsService/CalloutsService';
import { WithNavigationService } from 'v5/services/internal/NavigationService/NavigationService';
import { WithUserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { WithMutationService } from 'v5/services/internal/MutationService/MutationService';
import { WithMessageService } from 'v5/services/internal/MessageService/MessageService';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithEnterpriseMutationService } from 'v5/services/io/EnterpriseMutationService/EnterpriseMutationService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithResourceMutationService } from 'v5/services/io/ResourceMutationService/ResourceMutationService';
import { WithAccessService } from 'v5/services/front/AccessService/AccessService';
import { WithResourceTypeBindingsService } from 'v5/services/internal/ResourceTypeBindingsService/ResourceTypeBindingsService';
import { WithGlobalSettingsService } from 'v5/services/internal/GlobalSettingsService/GlobalSettingsService';
import { WithGeolocationService } from 'v5/services/io/GeolocationService/GeolocationService';
import { ResourceCreateDialog } from 'v5/features/resources/form/ResourceCreateDialog';
import type { ResourceMembership } from 'v5/core/entity-schema/entitySchema.types';
import { WithProductionMutationService } from 'v5/services/io/ProductionMutationService/ProductionMutationService';
import { WithUploadService } from 'v5/services/io/UploadService/UploadService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { CreateEnterpriseWorkspaceDialog } from 'v5/features/core/enterprise/management/CreateEnterpriseWorkspaceDialog';
import { EditEnterpriseWorkspaceDialog } from 'v5/features/core/enterprise/management/EditEnterpriseWorkspaceDialog';
import { CreateEnterpriseTeamDialog } from 'v5/features/core/enterprise/management/CreateEnterpriseTeamDialog';
import { EditEnterpriseTeamDialog } from 'v5/features/core/enterprise/management/EditEnterpriseTeamDialog';
import { TransferCalloutDialog } from 'v5/features/callouts/TransferCalloutDialog';
import { AssignResourcesToWorkspacesDialog } from 'v5/features/resources/AssignResourcesToWorkspacesDialog';
import { TransferProductionDialog } from 'v5/features/productions/TransferProductionDialog';
import { TransferCallsheetDialog } from 'v5/features/callsheets/TransferCallsheetDialog';
import { ChooseTeamDialog } from 'v5/patterns/forms/dialogs/ChooseTeamDialog';
import { getProductionDetailPath } from 'v5/core/app-scope/entityWorkspaceRouting';
import { internalInstance } from 'v5/i18n.internal';
import { appRouting } from 'v5/core/routing/appRouting';
import { WithAvailabilityRequestService } from 'v5/services/io/AvailabilityRequestService/AvailabilityRequestService';
import { WithFormatService } from 'cockpit/services/format/FormatService';
import { RequestAvailabilityDialog } from 'v5/features/availability-request/RequestAvailabilityDialog';
import { DeleteConfirmationDialog } from 'v5/patterns/forms/dialogs/DeleteConfirmationDialog';
import { SendBookingConfirmationDialog } from 'v5/features/send-booking-confirmation/SendBookingConfirmationDialog';
import { WithSystemSettingsService } from 'v5/services/internal/SystemSettingsService/SystemSettingsService';
import { WithBookingMutationService } from 'v5/services/io/BookingMutationService/BookingMutationService';
import { WithBookingService } from 'v5/services/io/BookingService/BookingService';
import { WithShortlistMutationService } from 'v5/services/io/ShortlistMutationService/ShortlistMutationService';
import { ShortlistCreateDialog } from 'v5/features/shortlists/create/ShortlistCreateDialog';
import { openReportFile } from 'v5/features/reporting/openReportFile';

function isMetastructureCustomField(
  field: MetastructureField
): field is Extract<
  MetastructureField,
  { type: MetastructureFieldType.Custom }
> {
  return field.type === MetastructureFieldType.Custom;
}

function getOrderedAssignedCustomFieldIds(
  fields: MetastructureField[]
): number[] {
  // Soft-archived (`active: false`) fields stay on the metastructure but are
  // not shown as currently assigned in the add/assign dialog.
  return fields
    .filter(
      (
        field
      ): field is Extract<
        MetastructureField,
        { type: MetastructureFieldType.Custom }
      > => isMetastructureCustomField(field) && field.active
    )
    .map(field => field.custom_field_definition_id);
}

function createUserFlowServiceInternals(
  config: Parameters<typeof createUserFlowService>[0]
) {
  async function resolveEnterpriseWorkspaceIdForCallout(
    appScope: AppScope,
    explicit?: Maybe<number>
  ): Promise<number | undefined> {
    if (explicit != null) {
      return explicit;
    }
    const isEnterprise = await config.services.featureService.ensureFeature(
      appScopeUtils.identityFromEntityWorkspace(appScope.organizationId, null),
      'enterprise'
    );
    if (!isEnterprise) {
      return undefined;
    }
    const workspaceSlug = appScopeUtils.getWorkspaceSlug(appScope);
    if (maybe.isAbsent(workspaceSlug)) {
      return undefined;
    }
    const workspaces = await config.services.enterpriseService.ensureWorkspaces(
      appScope.organizationId
    );
    return workspaces.find(workspace => workspace.slug === workspaceSlug)?.id;
  }

  async function navigateToCalloutDetail(
    calloutId: number,
    appScope: AppScope,
    enterpriseWorkspaceId?: Maybe<number>
  ) {
    const resolvedWorkspaceId = await resolveEnterpriseWorkspaceIdForCallout(
      appScope,
      enterpriseWorkspaceId
    );
    const workspaces = await config.services.enterpriseService.ensureWorkspaces(
      appScope.organizationId
    );

    config.services.navigationService.navigate(
      getCalloutDetailPath(
        appScope.organizationId,
        {
          id: calloutId,
          enterprise_workspace_id: resolvedWorkspaceId ?? null
        },
        workspaces
      )
    );
  }

  return {
    resolveEnterpriseWorkspaceIdForCallout,
    navigateToCalloutDetail
  };
}

export function createUserFlowService(
  config: WithServices<
    [
      WithProductionService,
      WithProductionTypeService,
      WithMetastructureMappingService,
      WithResourceTypeService,
      WithResourceService,
      WithTagService,
      WithGroupsService,
      WithCallsheetService,
      WithCallsheetMutationService,
      WithCalloutMutationService,
      WithCalloutsService,
      WithUserPreferencesService,
      WithToastService,
      WithMutationService,
      WithMessageService,
      WithFeatureService,
      WithEnterpriseMutationService,
      WithUploadService,
      WithResourceMutationService,
      WithShortlistMutationService,
      WithProductionMutationService,
      WithEnterpriseService,
      WithNavigationService<Record<string, object>>,
      WithAppScopeService,
      WithAvailabilityRequestService,
      WithFormatService,
      WithSystemSettingsService,
      WithBookingService,
      WithBookingMutationService,
      WithAccessService,
      WithResourceTypeBindingsService,
      WithGlobalSettingsService,
      WithGeolocationService,
      WithReportTemplateMutationService,
      WithBookingTypeService
    ]
  >
): UserFlowService {
  /**
   * Feature flags are keyed by browsing identity (ids), while user flows
   * receive the slug scope of the opener. Resolve the workspace slug once.
   */
  async function resolveScopeIdentity(
    appScope: AppScope
  ): Promise<AppScopeIdentity> {
    if (appScope.level === 'organization') {
      return { level: 'organization', organizationId: appScope.organizationId };
    }
    const workspace =
      await config.services.enterpriseService.ensureWorkspaceBySlug(
        appScope.organizationId,
        appScope.workspaceSlug
      );
    return maybe.mapOrElse(
      workspace,
      workspace => ({
        level: 'workspace' as const,
        organizationId: appScope.organizationId,
        workspaceId: workspace.id
      }),
      {
        level: 'organization' as const,
        organizationId: appScope.organizationId
      }
    );
  }

  /** Legacy modal (flag `new` / `v5`): kept until the v6 dialog is the default. */
  function openLegacyCreateResourceModal(options: {
    appScope: AppScope;
    resourceTypeId?: number;
    enterpriseWorkspaceId?: Maybe<number>;
  }) {
    store.dispatch(
      openModal(
        'ResourceCreateModal',
        options?.resourceTypeId || options?.enterpriseWorkspaceId != null
          ? {
              ...(options?.resourceTypeId != null
                ? { resource_type_id: options.resourceTypeId }
                : {}),
              // Contacts are M2N — create graph insert uses join-row column name.
              ...(options?.enterpriseWorkspaceId != null
                ? {
                    enterprise_workspaces: [
                      {
                        enterprise_workspace_id: options.enterpriseWorkspaceId
                      }
                    ]
                  }
                : {})
            }
          : undefined,
        options.appScope
      )
    );
  }

  function openCreateResourceDialog(options: {
    appScope: AppScope;
    resourceTypeId?: number;
    enterpriseWorkspaceId?: Maybe<number>;
  }) {
    const membership: ResourceMembership = maybe.isPresent(
      options.enterpriseWorkspaceId
    )
      ? { type: 'workspaces', workspaceIds: [options.enterpriseWorkspaceId] }
      : { type: 'global' };
    config.services.messageService.mountElement.sendRequest({
      appScope: options.appScope,
      handler: mountProps => (
        <ResourceCreateDialog
          open={mountProps.open}
          onOpenChange={mountProps.onOpenChange}
          initialResourceTypeId={options.resourceTypeId ?? null}
          initialMembership={membership}
          // The form's agent field opens resources in the sidebar.
          services={{ ...config.services, userFlowService }}
          onCreated={(created, next) => {
            const goToProfile = async () => {
              const workspaces =
                await config.services.enterpriseService.ensureWorkspaces(
                  options.appScope.organizationId
                );
              config.services.navigationService.navigate(
                getContactDetailPath(
                  options.appScope.organizationId,
                  {
                    enterprise_workspace_id:
                      options.enterpriseWorkspaceId ?? null
                  },
                  workspaces,
                  created.id
                )
              );
            };
            // As in Linear: what was created, and a way to it — unless the
            // dialog is on its way there already.
            config.services.toastService.open({
              type: 'success',
              message: (
                <>
                  Resource created
                  <span className="mt-1 block truncate font-normal text-fg-muted">
                    {created.full_name ||
                      [created.first_name, created.last_name]
                        .filter(Boolean)
                        .join(' ')}
                  </span>
                </>
              ),
              action:
                next === 'go-to-profile'
                  ? undefined
                  : {
                      label: 'View resource',
                      onClick: () => void goToProfile()
                    }
            });
            if (next === 'go-to-profile') void goToProfile();
            // Closing / resetting is the dialog's own job (EntityCreateDialog).
          }}
        />
      )
    });
  }

  /** Legacy modal (flag `shortlist-v5` off): kept until the v5 dialog is the default. */
  function openLegacyCreateShortlistModal(
    options: Parameters<UserFlowService['openCreateShortlistModal']>[0]
  ) {
    store.dispatch(
      openModal(
        'ShortlistCreateModal',
        {
          create: true,
          shortlist: {
            title: '',
            tags: [],
            ...(options.enterpriseWorkspaceId != null
              ? { enterprise_workspace_id: options.enterpriseWorkspaceId }
              : {}),
            ...(options.enterpriseTeamId != null
              ? { enterprise_team_id: options.enterpriseTeamId }
              : {})
          }
        },
        options.appScope
      )
    );
  }

  /** As the legacy modal: stay in the team being browsed, else the shortlist's own home. */
  async function shortlistPathAfterCreate(
    appScope: AppScope,
    shortlist: Shortlist
  ): Promise<string> {
    const organizationId = appScope.organizationId;
    if (appScope.level === 'team') {
      return appRouting
        .org(organizationId)
        .workspace(appScope.workspaceSlug)
        .team(appScope.teamSlug)
        .shortlists.detail(shortlist.id);
    }
    try {
      return await getShortlistDetailPathAsync(organizationId, shortlist, {
        workspaces:
          await config.services.enterpriseService.ensureWorkspaces(
            organizationId
          ),
        enterpriseService: config.services.enterpriseService
      });
    } catch {
      // The shortlist exists; its organization path opens it from anywhere.
      return myRouting
        .forWorkspace(organizationId)
        .shortlists()
        .detail(shortlist.id);
    }
  }

  function openCreateShortlistDialog(
    options: Parameters<UserFlowService['openCreateShortlistModal']>[0]
  ) {
    config.services.messageService.mountElement.sendRequest({
      appScope: options.appScope,
      handler: mountProps => (
        <ShortlistCreateDialog
          {...mountProps}
          services={config.services}
          enterpriseWorkspaceId={options.enterpriseWorkspaceId ?? null}
          enterpriseTeamId={options.enterpriseTeamId ?? null}
          onCreated={shortlist =>
            void shortlistPathAfterCreate(options.appScope, shortlist).then(
              path =>
                // The new board opens its Add resources column on `add=1`.
                config.services.navigationService.navigate(`${path}?add=1`)
            )
          }
        />
      )
    });
  }

  const { resolveEnterpriseWorkspaceIdForCallout, navigateToCalloutDetail } =
    createUserFlowServiceInternals(config);

  const confirmArchive = (options: {
    appScope: AppScope;
    title: string;
    description: string;
    onConfirm: () => Promise<void>;
    successMessage: string;
  }) =>
    new Promise<void>(resolve => {
      config.services.messageService.mountElement.sendRequest({
        appScope: options.appScope,
        handler: mountProps => (
          <DeleteConfirmationDialog
            {...mountProps}
            title={options.title}
            description={options.description}
            onResolve={async confirmed => {
              if (confirmed) {
                try {
                  await options.onConfirm();
                  config.services.toastService.open({
                    type: 'success',
                    message: options.successMessage
                  });
                } catch (e) {
                  config.services.toastService.open({
                    type: 'error',
                    message: ensureError(e).message
                  });
                }
              }
              resolve();
            }}
          />
        )
      });
    });

  const getTargetInfo = (request: CreateCustomFieldScope) => {
    if (!request) {
      return null;
    }

    const coreStructureName = CORE_STRUCTURES[request.coreStructure]?.name;
    if (!coreStructureName) {
      throw new Error(`Unknown core structure: ${request.coreStructure}`);
    }
    const entityName =
      store.getState()[request.coreStructure]?.data[request.coreStructureTypeId]
        ?.name ?? '???';

    return {
      coreStructure: coreStructureName,
      coreStructureType: entityName
    };
  };

  const getTargetPrompt = (request: CreateCustomFieldScope) => {
    const targetInfo = getTargetInfo(request);
    return targetInfo ? (
      <>
        <Info className="size-5 mr-2 inline-block" />
        <span>This will be added to </span>
        <strong>
          {targetInfo.coreStructure}: {targetInfo.coreStructureType}
        </strong>
      </>
    ) : null;
  };

  const buildCustomFieldsDialogData = (
    request: ScopedToCoreStructureRequest
  ) => {
    const state = store.getState();
    const definitions = getCustomFieldDefinitions(state);
    const allCustomFields = definitions?.filter(field => !field.archived) ?? [];
    const structureType =
      state[request.coreStructure]?.data?.[request.coreStructureTypeId];
    const structureFields = (structureType?.metastructure?.fields ??
      []) as MetastructureField[];
    const initialAssignedOrder =
      getOrderedAssignedCustomFieldIds(structureFields);
    const catalog = allCustomFields.map(field => ({
      id: field.id,
      name: field.name,
      typeLabel: friendlyStringFromConst(field.data_type),
      // Timestamped.created_at is typed as Date; Redux keeps raw API JSON (ISO strings).
      created_at: field.created_at as unknown as string
    }));
    const groupCatalog = (getCustomFieldGroupDefinitions(state) ?? [])
      .filter(group => !group.archived)
      .map(group => ({
        id: group.id,
        name: group.name,
        memberCount: group.custom_field_definitions?.length ?? 0,
        isArray: group.is_array
      }));
    const initialAssignedGroupIds = structureFields.flatMap(field =>
      field.type === MetastructureFieldType.CustomGroup && field.active
        ? [field.custom_fieldgroup_definition_id]
        : []
    );

    return {
      catalog,
      initialAssignedOrder,
      groupCatalog,
      initialAssignedGroupIds
    };
  };

  const saveAssignedCustomFields = async (
    request: ScopedToCoreStructureRequest,
    assignedIds: number[],
    assignedGroupIds: number[]
  ) => {
    const structureType =
      store.getState()[request.coreStructure]?.data?.[
        request.coreStructureTypeId
      ];
    if (!structureType) {
      throw new Error(
        'Could not save field assignment: structure type is missing from application state.'
      );
    }

    const assignedSet = new Set(assignedIds);
    const assignedGroupSet = new Set(assignedGroupIds);
    const existingFields = (structureType.metastructure?.fields ??
      []) as MetastructureField[];
    const existingCustomIds = new Set<number>();
    const existingGroupIds = new Set<number>();
    // Unassigned custom fields and groups are soft-archived, never hard-removed.
    const nextFields: MetastructureField[] = existingFields.map(field => {
      switch (field.type) {
        case MetastructureFieldType.Core:
          return field;
        case MetastructureFieldType.Custom:
          existingCustomIds.add(field.custom_field_definition_id);
          return {
            ...field,
            active: assignedSet.has(field.custom_field_definition_id)
          };
        case MetastructureFieldType.CustomGroup:
          existingGroupIds.add(field.custom_fieldgroup_definition_id);
          return {
            ...field,
            active: assignedGroupSet.has(field.custom_fieldgroup_definition_id)
          };
      }
    });

    for (const id of assignedSet) {
      if (!existingCustomIds.has(id)) {
        nextFields.push({
          active: true,
          custom_field_definition_id: id,
          default: '',
          locked: false,
          required: false,
          type: MetastructureFieldType.Custom
        });
      }
    }
    for (const id of assignedGroupSet) {
      if (!existingGroupIds.has(id)) {
        nextFields.push({
          active: true,
          custom_fieldgroup_definition_id: id,
          default: '',
          locked: false,
          required: false,
          type: MetastructureFieldType.CustomGroup
        });
      }
    }

    const updateAction = CORE_STRUCTURES[request.coreStructure]?.updateAction;
    if (!updateAction) {
      throw new Error(
        'Could not save field assignment: this structure has no update action configured.'
      );
    }

    const result = await store.dispatch(
      updateAction(request.coreStructureTypeId, {
        ...structureType,
        metastructure: {
          ...structureType.metastructure,
          fields: nextFields
        }
      })
    );
    if (result.error) {
      throw ensureError(result.error);
    }
  };

  const openAssignCustomFieldsDialogImpl = async (
    request: AssignCustomFieldsDialogRequest
  ) => {
    try {
      config.services.messageService.mountElement.sendRequest({
        appScope: request.appScope,
        handler: mountProps => (
          <CustomFieldAddDialogWidget
            {...mountProps}
            services={config.services}
            prompt={request.prompt}
            catalog={request.catalog}
            initialAssignedOrder={request.initialAssignedOrder}
            groupCatalog={request.groupCatalog}
            initialAssignedGroupIds={request.initialAssignedGroupIds}
            onCreate={async field => {
              try {
                if (request.onCreate) {
                  await request.onCreate(field);
                } else {
                  await config.services.mutationService.createCustomField(
                    field,
                    undefined
                  );
                }
                config.services.toastService.open({
                  type: 'success',
                  message: 'Created custom field successfully'
                });
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
            onSaveFields={async (assignedIds, assignedGroupIds) => {
              try {
                await request.onSaveFields(assignedIds, assignedGroupIds);
                config.services.toastService.open({
                  type: 'success',
                  message: 'Updated fields successfully'
                });
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
          />
        )
      });
    } catch (e) {
      config.services.toastService.open({
        type: 'error',
        message: ensureError(e).message
      });
    }
  };

  // Shared callout creation logic
  const createCalloutFlow = async (
    data: CalloutCreateFormData,
    appScope: AppScope,
    enterpriseWorkspaceId?: Maybe<number>
  ) => {
    try {
      const resolvedWorkspaceId = await resolveEnterpriseWorkspaceIdForCallout(
        appScope,
        enterpriseWorkspaceId
      );
      const result = await config.services.calloutMutationService.createCallout(
        {
          title: data.title,
          slug: data.slug,
          resource_type_id: data.resource_type_id,
          mappings: data.mappings,
          ...(resolvedWorkspaceId != null
            ? { enterprise_workspace_id: resolvedWorkspaceId }
            : {})
        }
      );
      config.services.toastService.open({
        type: 'success',
        message: 'Callout created successfully'
      });
      return result.id;
    } catch (e) {
      config.services.toastService.open({
        type: 'error',
        message: ensureError(e).message
      });
      return undefined;
    }
  };

  const duplicateCalloutFlow = async (
    sourceCallout: Callout,
    data: CalloutDuplicateFormData
  ) => {
    try {
      const enterpriseWorkspaceId =
        sourceCallout.enterprise_workspace_id ?? undefined;
      const result =
        await config.services.calloutMutationService.duplicateCallout({
          sourceCallout,
          ...data,
          ...(enterpriseWorkspaceId != null
            ? { enterprise_workspace_id: enterpriseWorkspaceId }
            : {})
        });
      config.services.toastService.open({
        type: 'success',
        message: 'Callout duplicated successfully'
      });
      return result.id;
    } catch (e) {
      config.services.toastService.open({
        type: 'error',
        message: ensureError(e).message
      });
      return undefined;
    }
  };

  // Shared callsheet creation logic
  const createCallsheetFlow = async (
    workspace: string,
    request: Parameters<UserFlowService['createCallsheet']>[1]
  ) => {
    try {
      const productions = await Promise.all(
        request.selectedProductionIds.map(id =>
          config.services.productionService.ensureProduction(id)
        )
      );

      // Generate callsheet data based on mode (single date vs range)
      const dateSelection = request.dateSelection;
      const generatedData =
        dateSelection?.type === 'range'
          ? await config.services.callsheetService.generateCallsheetDataFromProductionsForRange(
              productions,
              {
                dateRange: { from: dateSelection.from, to: dateSelection.to },
                callTime: request.generalCallTime
              }
            )
          : await config.services.callsheetService.generateCallsheetDataFromProductions(
              productions,
              {
                selectedDate: dateSelection?.date ?? request.date!,
                callTime: request.generalCallTime
              }
            );

      const result =
        await config.services.callsheetMutationService.createCallsheet(
          workspace,
          {
            ...request,
            generatedData: generatedData.data
          }
        );
      config.services.toastService.open({
        type: 'success',
        message: 'Callsheet created successfully'
      });
      return result.id;
    } catch (e) {
      config.services.toastService.open({
        type: 'error',
        message: ensureError(e).message
      });
    }
  };

  const userFlowService: UserFlowService = {
    createCustomField: async request => {
      appScopeUtils.assertValid(request.appScope);
      const { appScope, ...rest } = request;
      const mutationScope: CreateCustomFieldScope =
        'type' in rest ? rest : undefined;
      const channel =
        await config.services.messageService.createCustomField.sendRequest({
          type: 'create',
          prompt: getTargetPrompt(mutationScope),
          appScope
        });

      let result: Maybe<{ id: number }> = maybe.ofAbsent();

      for await (const payload of channel) {
        try {
          result = await config.services.mutationService.createCustomField(
            payload,
            mutationScope
          );
          config.services.toastService.open({
            type: 'success',
            message: 'Created custom field successfully'
          });
          channel.emit(mt.ofSuccess(void 0, void 0));
        } catch (e) {
          channel.emit(mt.ofError(void 0, ensureError(e)));
          config.services.toastService.open({
            type: 'error',
            message: ensureError(e).message
          });
        }
      }

      return maybe.mapOrElse(
        result,
        result => ({ action: 'created', id: result.id }),
        { action: 'canceled' }
      );
    },
    editCustomField: async (id, payload, options) => {
      appScopeUtils.assertValid(options.appScope);
      const channel =
        await config.services.messageService.createCustomField.sendRequest({
          type: 'edit',
          customField: payload,
          appScope: options.appScope
        });

      let created = false;

      for await (const payload of channel) {
        try {
          await config.services.mutationService.updateCustomField(id, payload);
          config.services.toastService.open({
            type: 'success',
            message: 'Updated custom field successfully'
          });
          created = true;
          channel.emit(mt.ofSuccess(void 0, void 0));
        } catch (e) {
          channel.emit(mt.ofError(void 0, ensureError(e)));
          config.services.toastService.open({
            type: 'error',
            message: ensureError(e).message
          });
        }
      }

      return created ? { action: 'updated' } : { action: 'canceled' };
    },
    editCustomFieldGroup: (definition, options) => {
      store.dispatch(
        openModal(
          'FieldGroupCreateModal',
          { field: definition },
          options.appScope
        )
      );
    },
    openAddCustomFieldsDialog: async request => {
      try {
        const { appScope, ...mutationRequest } = request;
        const {
          catalog,
          initialAssignedOrder,
          groupCatalog,
          initialAssignedGroupIds
        } = buildCustomFieldsDialogData(mutationRequest);
        await openAssignCustomFieldsDialogImpl({
          appScope,
          prompt: getTargetPrompt(mutationRequest),
          catalog,
          initialAssignedOrder,
          groupCatalog,
          initialAssignedGroupIds,
          onCreate: async field => {
            await config.services.mutationService.createCustomField(
              field,
              mutationRequest
            );
          },
          onSaveFields: async (assignedIds, assignedGroupIds) => {
            await saveAssignedCustomFields(
              mutationRequest,
              assignedIds,
              assignedGroupIds
            );
          }
        });
      } catch (e) {
        config.services.toastService.open({
          type: 'error',
          message: ensureError(e).message
        });
      }
    },
    openAssignCustomFieldsDialog: request => {
      void openAssignCustomFieldsDialogImpl(request);
    },
    openCreateCallsheetDialog: (workspace, options) => {
      const { appScope, ...dialogOptions } = options;
      const openInScope = (scope: AppScope) =>
        config.services.messageService.mountElement.sendRequest({
          appScope: scope,
          handler: mountProps => (
            <CreateCallsheetDialog
              {...mountProps}
              {...dialogOptions}
              onSubmit={async data => {
                const id = await createCallsheetFlow(workspace, data);
                mountProps.close();
                if (maybe.isPresent(id)) {
                  config.services.navigationService.navigate(
                    await getCallsheetDetailPathAsync(
                      scope.organizationId,
                      {
                        id,
                        enterprise_workspace_id: data.enterprise_workspace_id,
                        enterprise_team_id: data.enterprise_team_id
                      },
                      { enterpriseService: config.services.enterpriseService }
                    )
                  );
                }
              }}
              services={config.services}
            />
          )
        });
      if (appScope.level === 'team') {
        openInScope(appScope);
        return;
      }
      // A callsheet belongs to a team: outside one, the team is chosen first.
      // An organization without workspaces has no teams and creates as before.
      void config.services.enterpriseService
        .ensureWorkspaces(appScope.organizationId)
        .then(workspaces => {
          if (workspaces.length === 0) {
            openInScope(appScope);
            return;
          }
          const t = internalInstance.getFixedT(
            null,
            'callsheets',
            'ChooseCallsheetTeam'
          );
          config.services.messageService.mountElement.sendRequest({
            appScope,
            handler: mountProps => (
              <ChooseTeamDialog
                {...mountProps}
                organizationId={appScope.organizationId}
                initialWorkspace={
                  // The workspace in the URL, or the one the sidebar shows on an
                  // organization page (the last browsed, else the first).
                  workspaces.find(
                    candidate =>
                      candidate.slug ===
                      (appScope.level === 'workspace'
                        ? appScope.workspaceSlug
                        : config.services.appScopeService.getRememberedWorkspaceSlug())
                  ) ??
                  workspaces[0] ??
                  null
                }
                titleMessage={t('title')}
                descriptionMessage={t('description')}
                services={config.services}
                onChoose={({ workspace: chosen, team }) => {
                  mountProps.close();
                  openInScope({
                    level: 'team',
                    organizationId: appScope.organizationId,
                    workspaceSlug: chosen.slug,
                    teamSlug: team.slug
                  });
                }}
              />
            )
          });
        })
        .catch(e =>
          config.services.toastService.open({
            type: 'error',
            message: ensureError(e).message
          })
        );
    },
    createCallsheet: createCallsheetFlow,
    openDuplicateCallsheetDialog: async (workspace, callsheetId, options) => {
      try {
        // Fetch the original callsheet
        const originalCallsheet =
          await config.services.callsheetService.ensureCallsheet(callsheetId);

        config.services.messageService.mountElement.sendRequest({
          appScope: options.appScope,
          handler: mountProps => (
            <DuplicateCallsheetDialog
              {...mountProps}
              originalCallsheet={originalCallsheet}
              onSubmit={async data => {
                const id = await duplicateCallsheetFlow(workspace, data);
                mountProps.close();
                if (maybe.isPresent(id)) {
                  // The copy lives where the original does.
                  config.services.navigationService.navigate(
                    await getCallsheetDetailPathAsync(
                      options.appScope.organizationId,
                      {
                        id,
                        enterprise_workspace_id:
                          originalCallsheet.enterprise_workspace_id,
                        enterprise_team_id: originalCallsheet.enterprise_team_id
                      },
                      { enterpriseService: config.services.enterpriseService }
                    )
                  );
                }
              }}
              services={config.services}
            />
          )
        });
      } catch (e) {
        config.services.toastService.open({
          type: 'error',
          message: ensureError(e).message
        });
      }
    },
    duplicateCallsheet: duplicateCallsheetFlow,
    openResourceSidebar: (resourceId, options) => {
      // For now, create a minimal resource object with just the ID
      // The sidebar will load the full resource data
      const minimalResource = { id: resourceId };
      store.dispatch(openContactSidebar(minimalResource, options.appScope));
    },

    openProductionSidebar: (productionId, options) => {
      store.dispatch(openProductionSidebar({ id: productionId }, options));
    },

    openBookingSidebar: (bookingId, options) => {
      const minimalBooking = { id: bookingId };
      store.dispatch(openBookingSidebar(minimalBooking, options));
    },

    openExpenseReceiptsModal: ({
      expenseId,
      productionId,
      budgetExpenseId,
      appScope
    }) => {
      store.dispatch(
        openModal(
          'ExpenseReceiptsModal',
          { expenseId, productionId, budgetExpenseId },
          appScope
        )
      );
    },

    openEditExpenseModal: ({
      expenseId,
      productionBudgetExpenseId,
      productionId,
      appScope,
      expense: expensePayload,
      production: productionPayload
    }) => {
      store.dispatch(
        openModal(
          'ExpenseCreateModal',
          {
            expense: expensePayload
              ? {
                  ...expensePayload,
                  id: expenseId,
                  production_budget_expense_id: productionBudgetExpenseId
                }
              : {
                  id: expenseId,
                  production_budget_expense_id: productionBudgetExpenseId
                },
            production: productionPayload ?? { id: productionId }
          },
          appScope
        )
      );
    },

    openCreateBookingModal: options => {
      store.dispatch(
        openModal('ResourceEventsModal', undefined, options.appScope)
      );
    },

    openCreateResourceModal: options => {
      void resolveScopeIdentity(options.appScope)
        .then(identity =>
          config.services.featureService.ensureFeature(
            identity,
            'resource-pane'
          )
        )
        .catch(() => 'v6' as const)
        .then(flag => {
          if (flag === 'v6') {
            openCreateResourceDialog(options);
          } else {
            openLegacyCreateResourceModal(options);
          }
        });
    },

    openCreateProductionModal: options =>
      store.dispatch(
        openModal(
          'ProductionSetUpModal',
          {
            create: true,
            ...(options.date
              ? {
                  startDate: options.date.toString(),
                  endDate: options.date.toString()
                }
              : {}),
            production: {
              ...(options.enterpriseWorkspaceId != null
                ? { enterprise_workspace_id: options.enterpriseWorkspaceId }
                : {}),
              ...(options.enterpriseTeamId != null
                ? { enterprise_team_id: options.enterpriseTeamId }
                : {})
            }
          },
          options.appScope
        )
      ),
    openCreateShortlistModal: options => {
      // The flag of the scope the new shortlist will belong to, not the browsing one.
      void config.services.featureService
        .ensureFeature(
          appScopeUtils.identityFromEntityWorkspace(
            options.appScope.organizationId,
            options.enterpriseWorkspaceId
          ),
          'shortlist-v5'
        )
        // A flag that fails to load is off: today's modal.
        .catch(() => false)
        .then(isV5 => {
          if (isV5) {
            openCreateShortlistDialog(options);
          } else {
            openLegacyCreateShortlistModal(options);
          }
        });
    },

    shareShortlist: (publicId, workspace) => {
      if (!publicId) {
        config.services.toastService.open({
          type: 'error',
          message: 'This shortlist does not have a public link'
        });
        return;
      }

      const id = publicId.toLowerCase();
      const { origin } = window.location;

      const publicUrl = `${origin}/app/${workspace}/p/shortlists/${id}`;
      window.open(publicUrl, '_blank');
    },

    openCreateCalloutModal: async options => {
      config.services.messageService.mountElement.sendRequest({
        appScope: options.appScope,
        handler: mountProps => (
          <CreateCalloutDialog
            {...mountProps}
            enterpriseWorkspaceId={options.enterpriseWorkspaceId}
            onSubmit={async data => {
              const id = await createCalloutFlow(
                data,
                options.appScope,
                options.enterpriseWorkspaceId
              );
              mountProps.close();
              if (maybe.isPresent(id)) {
                await navigateToCalloutDetail(
                  id,
                  options.appScope,
                  options.enterpriseWorkspaceId
                );
              }
            }}
            services={config.services}
          />
        )
      });
    },

    openEditCalloutDialog: async (callout, options) => {
      config.services.messageService.mountElement.sendRequest({
        appScope: options.appScope,
        handler: mountProps => (
          <EditCalloutDialog
            {...mountProps}
            callout={callout}
            onSubmit={async data => {
              try {
                await config.services.calloutMutationService.updateCallout(
                  callout.id,
                  {
                    title: data.title,
                    resource_type_id: data.resource_type_id,
                    ...(data.slug ? { slug: data.slug } : {})
                  }
                );
                config.services.toastService.open({
                  type: 'success',
                  message: 'Callout updated successfully'
                });
                mountProps.close();
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
            services={config.services}
          />
        )
      });
    },

    openDuplicateCalloutDialog: async (callout, options) => {
      config.services.messageService.mountElement.sendRequest({
        appScope: options.appScope,
        handler: mountProps => (
          <DuplicateCalloutDialog
            {...mountProps}
            callout={callout}
            onSubmit={async data => {
              const id = await duplicateCalloutFlow(callout, data);
              mountProps.close();
              if (maybe.isPresent(id)) {
                await navigateToCalloutDetail(
                  id,
                  options.appScope,
                  callout.enterprise_workspace_id ?? undefined
                );
              }
            }}
            services={config.services}
          />
        )
      });
    },

    shareCallout: async (callout, workspace) => {
      const workspaces =
        await config.services.enterpriseService.ensureWorkspaces(workspace);

      const publicUrl = getCalloutPublicUrl(workspace, callout, {
        workspaces
      });

      if (!publicUrl) {
        config.services.toastService.open({
          type: 'error',
          message: 'This callout does not have a public link'
        });
        return;
      }

      const { origin } = window.location;
      window.open(`${origin}${publicUrl}`, '_blank');
    },

    openReportTemplateEditor: ({ appScope, mode }) => {
      config.services.messageService.mountElement.sendRequest({
        appScope,
        handler: mountProps => (
          <ReportTemplateEditorDialog
            {...mountProps}
            services={config.services}
            mode={mode}
            onSubmit={async draft => {
              switch (mode.type) {
                case 'create':
                  await config.services.reportTemplateMutationService.createTemplate(
                    draft
                  );
                  return;
                case 'edit':
                  await config.services.reportTemplateMutationService.updateTemplate(
                    mode.template.id,
                    draft
                  );
                  return;
                case 'view':
                  return;
              }
            }}
          />
        )
      });
    },
    openDownloadReportDialog: ({
      appScope,
      template,
      organizationId,
      organization
    }) => {
      config.services.messageService.mountElement.sendRequest({
        appScope,
        handler: mountProps => (
          <DownloadReportDialog
            {...mountProps}
            services={config.services}
            organizationId={organizationId}
            organization={organization}
            templateName={template.name}
            onSubmit={async scope => {
              const { accessUrl } =
                await config.services.reportTemplateMutationService.exportTemplate(
                  template.id,
                  scope
                );
              openReportFile(accessUrl, config.services.toastService);
              mountProps.onOpenChange(false);
            }}
          />
        )
      });
    },
    downloadReport: async (templateId, scope) => {
      try {
        const { accessUrl } =
          await config.services.reportTemplateMutationService.exportTemplate(
            templateId,
            scope
          );
        openReportFile(accessUrl, config.services.toastService);
      } catch (e) {
        config.services.toastService.open({
          type: 'error',
          message: ensureError(e).message
        });
        throw e;
      }
    },
    openCreateEnterpriseWorkspaceDialog: (organizationId, options) => {
      config.services.messageService.mountElement.sendRequest({
        appScope: options.appScope,
        handler: mountProps => (
          <CreateEnterpriseWorkspaceDialog
            {...mountProps}
            services={config.services}
            onSubmit={async data => {
              try {
                const workspace =
                  await config.services.enterpriseMutationService.createWorkspace(
                    {
                      organizationId,
                      name: data.name.trim(),
                      slug: undefined,
                      iconUrl: data.iconUrl.trim() || null
                    }
                  );
                config.services.toastService.open({
                  type: 'success',
                  message: 'Workspace created'
                });
                options.onCreated?.(workspace);
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
          />
        )
      });
    },

    openEditEnterpriseWorkspaceDialog: (organizationId, workspace, options) => {
      config.services.messageService.mountElement.sendRequest({
        appScope: options.appScope,
        handler: mountProps => (
          <EditEnterpriseWorkspaceDialog
            {...mountProps}
            services={config.services}
            workspace={workspace}
            onSubmit={async data => {
              try {
                await config.services.enterpriseMutationService.updateWorkspace(
                  {
                    organizationId,
                    id: workspace.id,
                    name: data.name.trim(),
                    iconUrl: data.iconUrl.trim() || null
                  }
                );
                config.services.toastService.open({
                  type: 'success',
                  message: 'Workspace updated'
                });
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
          />
        )
      });
    },

    openCreateEnterpriseTeamDialog: ({
      organizationId,
      workspaceId,
      appScope,
      onCreated
    }) => {
      config.services.messageService.mountElement.sendRequest({
        appScope,
        handler: mountProps => (
          <CreateEnterpriseTeamDialog
            {...mountProps}
            services={config.services}
            onSubmit={async data => {
              try {
                const team =
                  await config.services.enterpriseMutationService.createTeam({
                    organizationId,
                    workspaceId,
                    name: data.name.trim(),
                    slug: undefined,
                    iconUrl: data.iconUrl.trim() || null,
                    iconColor: data.iconColor.trim() || null
                  });
                config.services.toastService.open({
                  type: 'success',
                  message: 'Team created'
                });
                onCreated?.(team);
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
          />
        )
      });
    },

    openEditEnterpriseTeamDialog: (organizationId, team, options) => {
      config.services.messageService.mountElement.sendRequest({
        appScope: options.appScope,
        handler: mountProps => (
          <EditEnterpriseTeamDialog
            {...mountProps}
            services={config.services}
            team={team}
            onSubmit={async data => {
              try {
                await config.services.enterpriseMutationService.updateTeam({
                  organizationId,
                  id: team.id,
                  name: data.name.trim(),
                  iconUrl: data.iconUrl.trim() || null,
                  iconColor: data.iconColor.trim() || null
                });
                config.services.toastService.open({
                  type: 'success',
                  message: 'Team updated'
                });
              } catch (e) {
                config.services.toastService.open({
                  type: 'error',
                  message: ensureError(e).message
                });
                throw e;
              }
            }}
          />
        )
      });
    },

    archiveEnterpriseWorkspace: (organizationId, workspace, options) => {
      void confirmArchive({
        appScope: options.appScope,
        title: 'Archive workspace',
        description:
          'Archive this workspace? Teams and data remain but it will be hidden from navigation.',
        successMessage: 'Workspace archived',
        onConfirm: async () => {
          await config.services.enterpriseMutationService.archiveWorkspace({
            organizationId,
            id: workspace.id,
            archived: undefined
          });
        }
      });
    },

    archiveEnterpriseTeam: (organizationId, team, options) => {
      void confirmArchive({
        appScope: options.appScope,
        title: 'Archive team',
        description: 'Archive this team? It will be hidden from navigation.',
        successMessage: 'Team archived',
        onConfirm: async () => {
          await config.services.enterpriseMutationService.archiveTeam({
            organizationId,
            id: team.id,
            archived: undefined
          });
        }
      });
    },

    unarchiveEnterpriseWorkspace: (organizationId, workspace) => {
      void config.services.enterpriseMutationService
        .archiveWorkspace({
          organizationId,
          id: workspace.id,
          archived: false
        })
        .then(() => {
          config.services.toastService.open({
            type: 'success',
            message: 'Workspace restored'
          });
        })
        .catch(error => {
          config.services.toastService.open({
            type: 'error',
            message: ensureError(error).message
          });
        });
    },

    unarchiveEnterpriseTeam: (organizationId, team) => {
      void config.services.enterpriseMutationService
        .archiveTeam({
          organizationId,
          id: team.id,
          archived: false
        })
        .then(() => {
          config.services.toastService.open({
            type: 'success',
            message: 'Team restored'
          });
        })
        .catch(error => {
          config.services.toastService.open({
            type: 'error',
            message: ensureError(error).message
          });
        });
    },

    openAssignResourcesToWorkspacesDialog: ({
      appScope,
      organizationId,
      resourceIds,
      resourceName,
      initialWorkspaceIds = [null]
    }) => {
      config.services.messageService.mountElement.sendRequest({
        appScope,
        handler: mountProps => (
          <AssignResourcesToWorkspacesDialog
            {...mountProps}
            organizationId={organizationId}
            resourceName={resourceName}
            resourceCount={resourceIds.length}
            initialWorkspaceIds={initialWorkspaceIds}
            services={config.services}
            onSubmit={async workspaceIds => {
              await config.services.resourceMutationService.assignResourcesToWorkspaces(
                {
                  ids: resourceIds,
                  enterprise_workspace_ids: workspaceIds
                }
              );
              config.services.toastService.open({
                type: 'success',
                message: internalInstance.getFixedT(
                  null,
                  'resources',
                  'AssignResourcesToWorkspacesDialog'
                )('success', { count: resourceIds.length })
              });
              mountProps.close();
              store.dispatch(closeContactSidebar());
            }}
          />
        )
      });
    },

    openTransferCalloutDialog: ({
      appScope,
      organizationId,
      calloutIds,
      calloutName,
      currentWorkspaceId
    }) => {
      const isBatch = calloutIds.length > 1;
      config.services.messageService.mountElement.sendRequest({
        appScope,
        handler: mountProps => (
          <TransferCalloutDialog
            {...mountProps}
            organizationId={organizationId}
            calloutName={calloutName}
            calloutCount={calloutIds.length}
            currentWorkspaceId={currentWorkspaceId}
            services={config.services}
            onSubmit={async workspace => {
              await config.services.calloutMutationService.transferCalloutsToWorkspace(
                {
                  ids: calloutIds,
                  enterprise_workspace_id: workspace.id
                }
              );
              config.services.toastService.open({
                type: 'success',
                message: internalInstance.getFixedT(
                  null,
                  'callouts',
                  'TransferCalloutDialog'
                )('success', { count: calloutIds.length })
              });
              mountProps.close();
              if (isBatch) {
                config.services.navigationService.navigate(
                  appRouting
                    .org(organizationId)
                    .workspace(workspace.slug)
                    .callouts.root()
                );
              } else {
                config.services.navigationService.navigate(
                  appRouting
                    .org(organizationId)
                    .workspace(workspace.slug)
                    .callouts.detail(calloutIds[0]!)
                );
              }
            }}
          />
        )
      });
    },

    openTransferProductionDialog: ({
      appScope,
      organizationId,
      productionIds,
      productionName,
      currentWorkspaceId,
      currentTeamId
    }) => {
      const isBatch = productionIds.length > 1;
      config.services.messageService.mountElement.sendRequest({
        appScope,
        handler: mountProps => (
          <TransferProductionDialog
            {...mountProps}
            organizationId={organizationId}
            productionName={productionName}
            productionCount={productionIds.length}
            currentWorkspaceId={currentWorkspaceId}
            currentTeamId={currentTeamId}
            services={config.services}
            onSubmit={async ({ workspace, team }) => {
              await config.services.productionMutationService.transferProductionsToScope(
                {
                  ids: productionIds,
                  enterprise_workspace_id: workspace.id,
                  enterprise_team_id: team.id
                }
              );
              config.services.toastService.open({
                type: 'success',
                message: internalInstance.getFixedT(
                  null,
                  'productions',
                  'TransferProductionDialog'
                )('success', { count: productionIds.length })
              });
              mountProps.close();
              if (isBatch) {
                config.services.navigationService.navigate(
                  appRouting
                    .org(organizationId)
                    .workspace(workspace.slug)
                    .team(team.slug)
                    .productions.root()
                );
              } else {
                config.services.navigationService.navigate(
                  getProductionDetailPath(
                    organizationId,
                    {
                      id: productionIds[0]!,
                      enterprise_workspace_id: workspace.id,
                      enterprise_team_id: team.id
                    },
                    [workspace],
                    [{ id: team.id, slug: team.slug }]
                  )
                );
              }
            }}
          />
        )
      });
    },

    openTransferCallsheetDialog: ({
      appScope,
      organizationId,
      callsheetIds,
      callsheetName,
      currentWorkspaceId,
      currentTeamId
    }) => {
      config.services.messageService.mountElement.sendRequest({
        appScope,
        handler: mountProps => (
          <TransferCallsheetDialog
            {...mountProps}
            organizationId={organizationId}
            callsheetName={callsheetName}
            callsheetCount={callsheetIds.length}
            currentWorkspaceId={currentWorkspaceId}
            currentTeamId={currentTeamId}
            services={config.services}
            onSubmit={async ({ workspace, team }) => {
              await config.services.callsheetMutationService.transferCallsheetsToScope(
                {
                  ids: callsheetIds,
                  enterprise_workspace_id: workspace.id,
                  enterprise_team_id: team.id
                }
              );
              config.services.toastService.open({
                type: 'success',
                message: internalInstance.getFixedT(
                  null,
                  'callsheets',
                  'TransferCallsheetDialog'
                )('success', { count: callsheetIds.length })
              });
              mountProps.close();
              config.services.navigationService.navigate(
                callsheetIds.length > 1
                  ? appRouting
                      .org(organizationId)
                      .workspace(workspace.slug)
                      .team(team.slug)
                      .callsheets.root()
                  : getCallsheetDetailPath(
                      organizationId,
                      {
                        id: callsheetIds[0]!,
                        enterprise_workspace_id: workspace.id,
                        enterprise_team_id: team.id
                      },
                      [workspace],
                      [team]
                    )
              );
            }}
          />
        )
      });
    },

    openRequestAvailabilityDialog: ({ resourceIds, appScope }) => {
      config.services.messageService.mountElement.sendRequest({
        appScope,
        handler: mountProps => (
          <RequestAvailabilityDialog
            {...mountProps}
            resourceIds={resourceIds}
            services={config.services}
          />
        )
      });
    },

    openSendBookingConfirmationDialog: options => {
      const { appScope } = options;
      config.services.messageService.mountElement.sendRequest({
        appScope,
        handler: mountProps => (
          <SendBookingConfirmationDialog
            {...mountProps}
            {...('productionId' in options
              ? { productionId: options.productionId }
              : { bookingId: options.bookingId })}
            appScope={appScope}
            services={config.services}
          />
        )
      });
    }
  };
  return userFlowService;

  // Duplicate callsheet flow
  async function duplicateCallsheetFlow(
    workspace: string,
    request: DuplicateCallsheetFormData
  ) {
    try {
      const result =
        await config.services.callsheetMutationService.duplicateCallsheet(
          workspace,
          request
        );

      config.services.toastService.open({
        type: 'success',
        message: 'Callsheet duplicated successfully'
      });
      return result.id;
    } catch (e) {
      config.services.toastService.open({
        type: 'error',
        message: ensureError(e).message
      });
    }
  }
}
